import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import GenerationNotificationModal from '../components/GenerationNotificationModal';

export const MarketingPlanReview = () => {
  const navigate = useNavigate();
  const [marketingAnalysis, setMarketingAnalysis] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvingPlan, setApprovingPlan] = useState(false);
  const [isAgentOn, setIsAgentOn] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  useEffect(() => {
    fetchMarketingPlan();
  }, []);

  const fetchMarketingPlan = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');

      // Fetch the marketing plan analysis and agent status
      const { data: configData, error: configError } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_analysis, media_agent_on')
        .eq('id', 0)
        .maybeSingle();

      if (configError) throw configError;

      if (configData && configData.media_agent_analysis) {
        setMarketingAnalysis(configData.media_agent_analysis);
        setIsAgentOn(configData.media_agent_on || false);
      } else {
        // No marketing plan found, redirect to brand description
        navigate('/redes-sociales/descripcion-brand');
      }
    } catch (err) {
      console.error('Error fetching marketing plan:', err);
      setError(err instanceof Error ? err.message : 'Error loading marketing plan');
    } finally {
      setLoading(false);
    }
  };

  const handleMarketingPlanApproval = async () => {
    try {
      setApprovingPlan(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Get user's id_company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      
      // Check if media_agent_on is already true
      const { data: configData } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .select('media_agent_on')
        .eq('id', 0)
        .maybeSingle();
      
      // Update the config to set media_agent_on to true
      const { error: updateError } = await supabase
        .schema((profile as any).id_company)
        .from('config')
        .update({ media_agent_on: true })
        .eq('id', 0);
      
      if (updateError) throw updateError;

      // Only send request to webhook if media_agent_on was not already true
      if (!configData?.media_agent_on) {
        await fetch('https://ai-agency-dt7d.onrender.com/webhook/ecdac889-7227-4a9a-8f5e-1e4786573968', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user.id,
            mode: 'start'
          }),
        });
      }

      // Show notification modal before navigating to calendar
      setShowNotificationModal(true);
    } catch (err) {
      console.error('Error handling marketing plan approval:', err);
      setError(err instanceof Error ? err.message : 'Error al aprobar el plan de marketing');
      setApprovingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 flex flex-col min-h-[80vh]">
      {/* Notification Modal */}
      <GenerationNotificationModal 
        isOpen={showNotificationModal} 
        onClose={() => {
          setShowNotificationModal(false);
          try { window.dispatchEvent(new Event('calendar-recheck')); } catch {}
          navigate('/redes-sociales/calendario');
        }} 
      />
      
      <div className="mb-6 md:mb-8 flex items-center gap-3">
        <Rocket className="w-6 h-6 md:w-8 md:h-8 text-gray-700" />
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Plan de Marketing</h1>
      </div>
      
      <div className="max-w-3xl mx-auto w-full space-y-4">
        {/* First message card - Introduction message */}
        <div className="bg-blue-50 rounded-md p-6 shadow-sm border border-blue-100">
          <p className="text-gray-800 whitespace-pre-wrap">
            {isAgentOn 
              ? "Este es el plan de marketing de tu marca. La inteligencia artificial se basará en el mismo para planear y generar las publicaciones para tu instagram" 
              : "Perfecto ahora vamos a armar un plan de marketing para tu marca"}
          </p>
        </div>
        
        {/* Second message card - Analysis content */}
        <div className="bg-white rounded-md p-6 shadow-sm border border-gray-200">
          <p className="text-gray-800 text-lg">{marketingAnalysis}</p>
        </div>
        
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-end pt-4">
          <button 
            onClick={() => navigate('/redes-sociales/marketing-plan-chat')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Quisiera cambiar algo
          </button>
          
          <button 
            onClick={handleMarketingPlanApproval}
            disabled={approvingPlan}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-normal"
          >
            {approvingPlan ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <span>Me parece bien</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingPlanReview;
